// Server-authoritative Display (OOH) screen config, with a localStorage
// write-through cache for offline resilience on unattended 24/7 screens.
// Source of truth: GET/POST /api/secure/display/my-screen-config.
import { getCPDataWithoutParams, postCPDataWithoutParams } from 'src/clinic/services'

const CACHE_KEY = 'display-screen-config'

export function readCachedDisplayConfig() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function cacheDisplayConfig(cfg) {
  try {
    if (cfg && cfg.clinicLocationId && cfg.mentorId) {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        clinicLocationId: cfg.clinicLocationId,
        mentorId: cfg.mentorId,
      }))
    }
  } catch {
    /* private-browsing / quota — cache is best-effort */
  }
}

// Returns { clinicLocationId, mentorId } from the server, or null. Throws on
// network error so callers can decide to fall back to the cache.
export async function fetchDisplayConfigFromServer() {
  const res = await getCPDataWithoutParams('/api/secure/display/my-screen-config')
  if (res?.status !== 200) {
    // A handled non-200 (e.g. 4xx) means "no config", not a transport failure.
    if (res?.status) return null
    throw new Error('network')
  }
  const assignment = res?.data?.data?.assignment
  if (assignment?.clinicLocationId && assignment?.mentorId) {
    return { clinicLocationId: assignment.clinicLocationId, mentorId: assignment.mentorId }
  }
  return null
}

export async function saveDisplayConfigToServer({ clinicLocationId, mentorId }) {
  const res = await postCPDataWithoutParams('/api/secure/display/my-screen-config', {
    clinicLocationId,
    mentorId,
  })
  if (res?.status === 200) {
    cacheDisplayConfig({ clinicLocationId, mentorId })
  }
  return res
}

// Legacy localStorage kiosk-config used before server-authoritative config.
// Read once to migrate existing display screens to the server.
export function readLegacyDisplayConfigFromKioskConfig() {
  try {
    const raw =
      localStorage.getItem('kiosk-config') || localStorage.getItem('ROLE_CA_CONFIG')
    const cfg = raw ? JSON.parse(raw) : null
    // Match on the presence of the OOH target fields (FLOW_ID value varies).
    if (cfg?.LOCATION_FOR_OOH && cfg?.MENTOR_FOR_OOH) {
      return { clinicLocationId: cfg.LOCATION_FOR_OOH, mentorId: cfg.MENTOR_FOR_OOH }
    }
    return null
  } catch {
    return null
  }
}
